import React from 'react'
import { ProductImage } from 'app/common/Common'
import TrashIcon from 'client/utils/icons/trash-icon'
import {
  ButtonsGroup,
  DcButton,
  DcFlexBox,
  DcInput,
  PixelFactoryContext,
  PixelModal,
  PixelSpinner,
  PixelTooltip,
  colors,
} from 'pixel-base'
import type { AjaxResponse } from 'rxjs/ajax'
import styled, { css } from 'styled-components'
import {
  batchUpdateWarehouseInventory,
  getWarehouseProductVariations,
} from 'utils/api-routes.util'

import { IPixelFactory } from '../../../../../../decl.d'

interface WarehouseInventoryBreakdown {
  on_hand: number
  available: number
  committed: number
  unavailable: number
}

export interface GroupedVariationRow {
  product_id: number
  product_name: string
  product_sku: string
  variation_type?: string | null
  variation_value?: string | null
  warehouse_inventory: WarehouseInventoryBreakdown
}

export interface GroupedParentProduct {
  product_id: number
  product_name: string
  product_sku?: string
  product_image?: string
  variation_count?: number
  warehouse_id?: number | string
}

interface GroupedProductVariationsProps {
  data: GroupedParentProduct
  warehouseId: number | string
  onSaved: () => void
  onClose: () => void
}

export interface GroupedProductVariationsHandle {
  requestClose: () => void
}

const inventoryValue = (
  row: GroupedVariationRow,
  field: keyof WarehouseInventoryBreakdown,
) =>
  row.warehouse_inventory?.[field] != null ? row.warehouse_inventory[field] : 0

const hasEnteredQuantity = (raw?: string) =>
  raw != null && String(raw).trim() !== ''

const getAdjustmentProducts = (
  variations: GroupedVariationRow[],
  counts: Record<number, string>,
  updateType: 'add' | 'set',
) =>
  variations
    .map((variation) => {
      const raw = counts[variation.product_id]
      if (!hasEnteredQuantity(raw)) {
        return null
      }
      const count = Number(raw)
      if (Number.isNaN(count) || count < 0) {
        return null
      }
      if (updateType === 'add' && count === 0) {
        return null
      }
      return {
        product_id: variation.product_id,
        count,
      }
    })
    .filter((product): product is { product_id: number; count: number } =>
      Boolean(product),
    )

const getVariationLabel = (row: GroupedVariationRow) => {
  const type = row.variation_type?.trim()
  const value = row.variation_value?.trim()
  if (type && value) {
    return `${type} / ${value}`
  }
  if (value) {
    return value
  }
  if (type) {
    return type
  }
  return row.product_sku || row.product_name || `--`
}

const formatSignedDelta = (delta: number) =>
  delta > 0 ? `+${delta}` : `${delta}`

interface PendingQuantityChange {
  product_id: number
  label: string
  sku?: string
  currentQty: number
  newQty: number
  delta: number
}

const getPendingQuantityChanges = (
  variations: GroupedVariationRow[],
  counts: Record<number, string>,
  updateType: 'add' | 'set',
): PendingQuantityChange[] => {
  const products = getAdjustmentProducts(variations, counts, updateType)
  const byId = new Map(
    variations.map((variation) => [variation.product_id, variation]),
  )

  return products
    .map((product): PendingQuantityChange | null => {
      const variation = byId.get(product.product_id)
      if (!variation) {
        return null
      }
      const currentQty = inventoryValue(variation, 'on_hand')
      const newQty =
        updateType === 'set' ? product.count : currentQty + product.count
      return {
        product_id: product.product_id,
        label: getVariationLabel(variation),
        sku: variation.product_sku || undefined,
        currentQty,
        newQty,
        delta: newQty - currentQty,
      }
    })
    .filter((change): change is PendingQuantityChange => change != null)
}

const GroupedProductVariations = React.forwardRef<
  GroupedProductVariationsHandle,
  GroupedProductVariationsProps
>(({ data, warehouseId, onSaved, onClose }, ref) => {
  const { showSuccess, showFailure } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [variations, setVariations] = React.useState<GroupedVariationRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [counts, setCounts] = React.useState<Record<number, string>>({})
  const [updateType, setUpdateType] = React.useState<'add' | 'set'>('add')
  const [notes, setNotes] = React.useState('')
  const [showDiscardConfirm, setShowDiscardConfirm] = React.useState(false)
  const [showSaveConfirm, setShowSaveConfirm] = React.useState(false)

  const hasUnsavedChanges = React.useMemo(() => {
    const hasQuantity = Object.values(counts).some(hasEnteredQuantity)
    return hasQuantity || notes.trim() !== ''
  }, [counts, notes])

  const pendingUpdateCount = React.useMemo(
    () => getAdjustmentProducts(variations, counts, updateType).length,
    [variations, counts, updateType],
  )

  const pendingQuantityChanges = React.useMemo(
    () => getPendingQuantityChanges(variations, counts, updateType),
    [variations, counts, updateType],
  )

  const pendingNotes = notes.trim()

  const savingRef = React.useRef(saving)
  const hasUnsavedRef = React.useRef(hasUnsavedChanges)
  const showDiscardRef = React.useRef(showDiscardConfirm)
  const showSaveConfirmRef = React.useRef(showSaveConfirm)
  savingRef.current = saving
  hasUnsavedRef.current = hasUnsavedChanges
  showDiscardRef.current = showDiscardConfirm
  showSaveConfirmRef.current = showSaveConfirm

  const requestClose = React.useCallback(() => {
    if (savingRef.current) {
      return
    }
    if (showDiscardRef.current || showSaveConfirmRef.current) {
      return
    }
    if (hasUnsavedRef.current) {
      showDiscardRef.current = true
      setShowDiscardConfirm(true)
      return
    }
    onClose()
  }, [onClose])

  React.useImperativeHandle(ref, () => ({ requestClose }), [requestClose])

  const handleKeepEditing = () => {
    showDiscardRef.current = false
    setShowDiscardConfirm(false)
  }

  const handleDiscardChanges = () => {
    showDiscardRef.current = false
    showSaveConfirmRef.current = false
    setShowDiscardConfirm(false)
    setShowSaveConfirm(false)
    onClose()
  }

  const handleCancelSaveConfirm = () => {
    showSaveConfirmRef.current = false
    setShowSaveConfirm(false)
  }

  const fetchVariations = React.useCallback(() => {
    if (!warehouseId || !data?.product_id) {
      return
    }

    setLoading(true)
    setLoadError(false)
    getWarehouseProductVariations(warehouseId, data.product_id).subscribe({
      next: (res) => {
        const typedRes = res as AjaxResponse<{
          data?: { variations?: GroupedVariationRow[] }
          error?: string
        }>
        if (typedRes.response?.error) {
          showFailure(typedRes.response.error)
          setVariations([])
          setLoadError(true)
        } else {
          setVariations(typedRes.response?.data?.variations || [])
          setLoadError(false)
        }
        setCounts({})
        setLoading(false)
      },
      error: () => {
        showFailure('Unable to load variations')
        setVariations([])
        setLoadError(true)
        setLoading(false)
      },
    })
  }, [data?.product_id, showFailure, warehouseId])

  React.useEffect(() => {
    setNotes('')
    setUpdateType('add')
    setShowDiscardConfirm(false)
    setShowSaveConfirm(false)
    showDiscardRef.current = false
    showSaveConfirmRef.current = false
  }, [data.product_id])

  React.useEffect(() => {
    fetchVariations()
  }, [fetchVariations])

  const executeSave = React.useCallback(() => {
    if (saving) {
      return
    }

    const products = getAdjustmentProducts(variations, counts, updateType)

    if (products.length === 0) {
      showFailure('Enter a quantity for at least one variation')
      return
    }

    if (!notes.trim()) {
      showFailure('Notes are required')
      return
    }

    setSaving(true)
    batchUpdateWarehouseInventory(warehouseId, {
      parent_product_id: data.product_id,
      update_type: updateType,
      status: 'on_hand',
      notes: notes.trim(),
      products,
    }).subscribe({
      next: (res) => {
        const response = res.response as
          | { message?: string; error?: string }
          | undefined
        if (response?.message) {
          showSuccess(response.message)
          setNotes('')
          setCounts({})
          onSaved()
          onClose()
        } else {
          setSaving(false)
          showFailure(response?.error || 'Unable to update inventory')
        }
      },
      error: () => {
        setSaving(false)
        showFailure('Unable to update inventory')
      },
    })
  }, [
    counts,
    data.product_id,
    notes,
    onClose,
    onSaved,
    saving,
    showFailure,
    showSuccess,
    updateType,
    variations,
    warehouseId,
  ])

  const handleSaveClick = () => {
    if (saving) {
      return
    }

    const products = getAdjustmentProducts(variations, counts, updateType)

    if (products.length === 0) {
      showFailure('Enter a quantity for at least one variation')
      return
    }

    if (!notes.trim()) {
      showFailure('Notes are required')
      return
    }

    showSaveConfirmRef.current = true
    setShowSaveConfirm(true)
  }

  const handleConfirmSave = () => {
    if (pendingQuantityChanges.length === 0) {
      showSaveConfirmRef.current = false
      setShowSaveConfirm(false)
      return
    }
    showSaveConfirmRef.current = false
    setShowSaveConfirm(false)
    executeSave()
  }

  const handleRemovePendingChange = (productId: number) => {
    setCounts((current) => {
      const next = { ...current }
      delete next[productId]
      return next
    })
  }

  React.useEffect(() => {
    if (!showSaveConfirm) {
      return
    }
    if (pendingQuantityChanges.length > 0) {
      return
    }
    // All pending quantity changes removed — leave the review modal without saving.
    showSaveConfirmRef.current = false
    setShowSaveConfirm(false)
  }, [pendingQuantityChanges.length, showSaveConfirm])

  const variationCount = data.variation_count ?? variations.length

  return (
    <DrawerLayout>
      <ProductHeader>
        <ProductImage
          style={{
            width: '52px',
            height: '52px',
            marginRight: 0,
            objectFit: 'contain',
            flexShrink: 0,
          }}
          path={data.product_image}
        />
        <ProductHeaderMeta>
          <ProductName title={data.product_name || undefined}>
            {data.product_name || 'Base Product'}
          </ProductName>
          <ProductSku title={data.product_sku || undefined}>
            SKU: {data.product_sku || '--'}
          </ProductSku>
          <ProductVariationCount>
            {variationCount} {variationCount === 1 ? 'Variation' : 'Variations'}
          </ProductVariationCount>
        </ProductHeaderMeta>
      </ProductHeader>
      <DrawerScroll>
        {loading ? (
          <StatusViewport>
            <PixelSpinner isShow />
          </StatusViewport>
        ) : loadError ? (
          <StatusViewport>
            <StatusTitle>Unable to load variations</StatusTitle>
            <StatusCopy>
              Something went wrong while loading inventory for this product.
            </StatusCopy>
            <DcButton
              variant="outline-primary"
              size="sm"
              onClick={fetchVariations}
            >
              Try again
            </DcButton>
          </StatusViewport>
        ) : variations.length === 0 ? (
          <StatusViewport>
            <StatusTitle>No active variations</StatusTitle>
            <StatusCopy>
              This product currently has no active variations.
            </StatusCopy>
          </StatusViewport>
        ) : (
          <InventorySection>
            <InventoryHeading>
              <InventoryTitle>Inventory</InventoryTitle>
              <InventoryMeta>
                {variations.length}{' '}
                {variations.length === 1 ? 'variation' : 'variations'}
              </InventoryMeta>
            </InventoryHeading>
            <VariationTable>
              <VariationGridHeader>
                <GridHeadCell>SKU</GridHeadCell>
                <GridHeadCell $align="center">Unavailable</GridHeadCell>
                <GridHeadCell $align="center">Committed</GridHeadCell>
                <GridHeadCell $align="center">Available</GridHeadCell>
                <GridHeadCell $align="center">On Hand</GridHeadCell>
                <GridHeadCell $editable>New Qty</GridHeadCell>
              </VariationGridHeader>
              {variations.map((row) => {
                const changed = hasEnteredQuantity(counts[row.product_id])
                return (
                  <VariationGridRow key={row.product_id} $changed={changed}>
                    <GridCell $changed={changed}>
                      <VariationSkuText title={row.product_sku || undefined}>
                        {row.product_sku || '--'}
                      </VariationSkuText>
                    </GridCell>
                    <GridCell $align="center" $changed={changed}>
                      <VariationStockText>
                        {inventoryValue(row, 'unavailable')}
                      </VariationStockText>
                    </GridCell>
                    <GridCell $align="center" $changed={changed}>
                      <VariationStockText>
                        {inventoryValue(row, 'committed')}
                      </VariationStockText>
                    </GridCell>
                    <GridCell $align="center" $changed={changed}>
                      <VariationStockText>
                        {inventoryValue(row, 'available')}
                      </VariationStockText>
                    </GridCell>
                    <GridCell $align="center" $changed={changed}>
                      <VariationStockText>
                        {inventoryValue(row, 'on_hand')}
                      </VariationStockText>
                    </GridCell>
                    <GridCell $editable $changed={changed}>
                      <QtyInputWrap>
                        <DcInput
                          inputSize="small"
                          height="33px"
                          width="100%"
                          type="number"
                          min={0}
                          isShowLabel={false}
                          name={`count-${row.product_id}`}
                          placeholder="0"
                          value={counts[row.product_id] ?? ''}
                          disabled={saving}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setCounts((current) => ({
                              ...current,
                              [row.product_id]: e.target.value,
                            }))
                          }}
                        />
                      </QtyInputWrap>
                    </GridCell>
                  </VariationGridRow>
                )
              })}
            </VariationTable>
          </InventorySection>
        )}
      </DrawerScroll>
      <DrawerFooter>
        <FooterPanel>
          <FooterField>
            <FieldLabel>Adjustment method</FieldLabel>
            <ButtonsGroup>
              <DcButton
                onClick={() => setUpdateType('add')}
                active={updateType === 'add'}
                variant={updateType === 'add' ? 'primary' : 'outline-primary'}
                size="sm"
                disabled={saving}
              >
                Add
              </DcButton>
              <DcButton
                onClick={() => setUpdateType('set')}
                active={updateType === 'set'}
                variant={updateType === 'set' ? 'primary' : 'outline-primary'}
                size="sm"
                disabled={saving}
              >
                Set
              </DcButton>
            </ButtonsGroup>
            <FieldHelper>Applies to the quantities entered above.</FieldHelper>
          </FooterField>
          <FooterField>
            <FieldLabel>Adjustment note</FieldLabel>
            <FieldHelper>
              Required — this note is recorded with the inventory adjustment.
            </FieldHelper>
            <NotesTextarea>
              <DcInput
                as="textarea"
                name="notes"
                placeholder="Add a note..."
                value={notes}
                isShowLabel={false}
                disabled={saving}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setNotes(e.target.value)
                }
              />
            </NotesTextarea>
          </FooterField>
          <FooterActions>
            {pendingUpdateCount > 0 ? (
              <ChangeSummary>
                {pendingUpdateCount}{' '}
                {pendingUpdateCount === 1 ? 'variation' : 'variations'} will be
                updated
              </ChangeSummary>
            ) : (
              <ChangeSummary aria-hidden="true" />
            )}
            <DcButton
              variant="outline-secondary"
              size="sm"
              onClick={requestClose}
              disabled={saving}
            >
              Cancel
            </DcButton>
            <DcButton
              size="sm"
              onClick={handleSaveClick}
              disabled={loading || saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </DcButton>
          </FooterActions>
        </FooterPanel>
      </DrawerFooter>
      <PixelModal
        show={showSaveConfirm}
        handleClose={handleCancelSaveConfirm}
        title="Save Changes?"
        size={'lg'}
        footer={
          <DcFlexBox justifyContent="flex-end" gap="8px" height="auto">
            <DcButton
              variant="outline-secondary"
              size="sm"
              onClick={handleCancelSaveConfirm}
              disabled={saving}
            >
              Cancel
            </DcButton>
            <DcButton
              variant="outline-danger"
              size="sm"
              onClick={handleDiscardChanges}
              disabled={saving}
            >
              Discard Changes
            </DcButton>
            <DcButton
              size="sm"
              onClick={handleConfirmSave}
              disabled={saving || pendingQuantityChanges.length === 0}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </DcButton>
          </DcFlexBox>
        }
      >
        <SaveConfirmBody>
          <DiscardCopy>
            You are about to save the following changes:
          </DiscardCopy>
          {pendingQuantityChanges.length > 0 && (
            <ChangeSummaryScroll>
              <ChangeSummaryTable>
                <thead>
                  <tr>
                    <th>Variation</th>
                    <th>Current Qty</th>
                    <th>New Qty</th>
                    <th>Change</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {pendingQuantityChanges.map((change) => (
                    <tr key={change.product_id}>
                      <td>
                        <VariationChangeLabel title={change.label}>
                          {change.label}
                        </VariationChangeLabel>
                        {change.sku ? (
                          <VariationChangeSku title={change.sku}>
                            {change.sku}
                          </VariationChangeSku>
                        ) : null}
                      </td>
                      <td>{change.currentQty}</td>
                      <td>{change.newQty}</td>
                      <td>
                        <ChangeDelta>
                          {change.currentQty} → {change.newQty} (
                          {formatSignedDelta(change.delta)})
                        </ChangeDelta>
                      </td>
                      <td>
                        <PixelTooltip
                          tooltipText="Remove change"
                          tooltipPlacement="top"
                        >
                          <RemoveChangeButton
                            type="button"
                            aria-label="Remove change"
                            disabled={saving}
                            onClick={() =>
                              handleRemovePendingChange(change.product_id)
                            }
                          >
                            <TrashIcon size={14} />
                          </RemoveChangeButton>
                        </PixelTooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ChangeSummaryTable>
            </ChangeSummaryScroll>
          )}
          {pendingNotes ? (
            <NoteChangeRow>
              <NoteChangeLabel>Note:</NoteChangeLabel>
              <NoteChangeValue title={pendingNotes}>
                <NoteEmpty>-</NoteEmpty>
                <NoteArrow>→</NoteArrow>
                <span>{pendingNotes}</span>
              </NoteChangeValue>
            </NoteChangeRow>
          ) : null}
        </SaveConfirmBody>
      </PixelModal>
      <PixelModal
        show={showDiscardConfirm}
        handleClose={handleKeepEditing}
        title="Discard changes?"
        size={'md'}
        footer={
          <DcFlexBox justifyContent="flex-end" gap="8px" height="auto">
            <DcButton
              variant="outline-secondary"
              size="sm"
              onClick={handleKeepEditing}
            >
              Keep Editing
            </DcButton>
            <DcButton
              variant="outline-danger"
              size="sm"
              onClick={handleDiscardChanges}
            >
              Discard Changes
            </DcButton>
          </DcFlexBox>
        }
      >
        <DiscardCopy>
          You have unsaved inventory changes.
          <br />
          Are you sure you want to leave?
        </DiscardCopy>
      </PixelModal>
    </DrawerLayout>
  )
})

GroupedProductVariations.displayName = 'GroupedProductVariations'

const VARIATION_GRID_TEMPLATE = 'minmax(148px, 1fr) 90px 90px 90px 90px 124px'

const variationGridColumns = css`
  display: grid;
  grid-template-columns: ${VARIATION_GRID_TEMPLATE};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  align-items: center;
`

const DrawerLayout = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: ${colors.white};
`

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  padding: 16px 24px;
  border-bottom: 1px solid ${colors.grayBorderLight};
  background-color: ${colors.white};
`

const ProductHeaderMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ProductName = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: ${colors.grayTextDark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProductSku = styled.div`
  font-size: 13px;
  line-height: 1.35;
  color: ${colors.grayInputDark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProductVariationCount = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: ${colors.grayMedium};
`

const DrawerScroll = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background-color: ${colors.white};
`

const InventorySection = styled.div`
  padding: 16px 24px 8px;
`

const InventoryHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
`

const InventoryTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: ${colors.grayTextDark};
`

const InventoryMeta = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: ${colors.grayMedium};
`

const DrawerFooter = styled.div`
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  padding: 16px 24px 20px;
  border-top: 1px solid ${colors.grayBorderLight};
  background-color: ${colors.white};
  display: flex;
  justify-content: flex-end;
`

const FooterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  max-width: 440px;
`

const FooterField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
`

const FieldLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  color: #495057;
`

const FieldHelper = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.grayMedium};
`

const NotesTextarea = styled.div`
  width: 100%;

  textarea {
    height: 80px !important;
    min-height: 80px !important;
  }
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const ChangeSummary = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: auto;
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.grayInputDark};
`

const VariationTable = styled.div`
  width: 100%;
  min-width: 0;
`

const VariationGridHeader = styled.div`
  ${variationGridColumns}
  position: sticky;
  top: 0;
  z-index: 1;
  min-height: 36px;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.grayBorderLight};
`

const VariationGridRow = styled.div<{ $changed?: boolean }>`
  ${variationGridColumns}
  min-height: 48px;
  border-bottom: 1px solid ${colors.grayBorderLight};
  background-color: ${({ $changed }) =>
    $changed ? colors.blueLighterBg : colors.white};

  &:last-of-type {
    border-bottom: none;
  }
`

const GridHeadCell = styled.div<{
  $align?: 'center' | 'left'
  $editable?: boolean
}>`
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ $editable }) =>
    $editable ? colors.grayTextDark : colors.grayMedium};
  text-align: ${({ $align, $editable }) =>
    $editable ? 'left' : $align || 'left'};
  ${({ $editable }) =>
    $editable &&
    css`
      border-left: 1px solid ${colors.grayBorderLight};
      background-color: ${colors.grayVeryLightBg};
    `}
`

const GridCell = styled.div<{
  $align?: 'center' | 'left'
  $editable?: boolean
  $changed?: boolean
}>`
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  background-color: ${({ $changed }) =>
    $changed ? colors.blueLighterBg : 'transparent'};
  justify-content: ${({ $align, $editable }) =>
    $editable ? 'flex-start' : $align === 'center' ? 'center' : 'flex-start'};
  ${({ $editable }) =>
    $editable &&
    css`
      border-left: 1px solid ${colors.grayBorderLight};
    `}
`

const StatusViewport = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 220px;
  padding: 32px 24px;
  text-align: center;
`

const StatusTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: ${colors.grayTextDark};
`

const StatusCopy = styled.div`
  max-width: 360px;
  font-size: 13px;
  line-height: 1.45;
  color: ${colors.grayMedium};
`

const VariationSkuText = styled.div`
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  color: ${colors.grayTextDark};
`

const VariationStockText = styled.div`
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  color: ${colors.grayMedium};
`

const QtyInputWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 104px;
  min-width: 0;
  margin-left: 0;
`

const DiscardCopy = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.grayTextDark};
`

const SaveConfirmBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`

const ChangeSummaryScroll = styled.div`
  max-height: 280px;
  overflow: auto;
  border: 1px solid ${colors.grayBorderLight};
  border-radius: 6px;
`

const ChangeSummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  line-height: 1.4;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${colors.grayBorderLight};
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    background-color: ${colors.white};
    font-weight: 600;
    color: ${colors.grayTextDark};
    text-align: left;
    z-index: 1;
  }

  th:nth-child(2),
  th:nth-child(3),
  th:nth-child(4),
  td:nth-child(2),
  td:nth-child(3),
  td:nth-child(4) {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  th:last-child,
  td:last-child {
    width: 40px;
    text-align: center;
    padding-left: 4px;
    padding-right: 8px;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`

const VariationChangeLabel = styled.div`
  font-weight: 500;
  color: ${colors.grayTextDark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
`

const VariationChangeSku = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${colors.grayMedium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
`

const ChangeDelta = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${colors.grayTextDark};
`

const NoteChangeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const NoteChangeLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.grayTextDark};
`

const NoteChangeValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${colors.grayTextDark};
  overflow: hidden;
`

const NoteEmpty = styled.span`
  color: ${colors.grayMedium};
`

const NoteArrow = styled.span`
  color: ${colors.grayMedium};
  flex-shrink: 0;
`

const RemoveChangeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${colors.danger};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${colors.dangerActive};
    background-color: ${colors.redLighterBg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export default GroupedProductVariations
